import * as RadixToggleGroup from '@radix-ui/react-toggle-group';
import * as yup from 'yup';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, TextField } from 'components';
import { getQueryError, handleApiResponse, useAddSystemMutation } from 'store';
import { useAlert, useToast } from 'context';
import { checkLicenseBits, inputFieldRegex, intTo16BitBin, requireValidFieldValue } from 'utils'; // eslint-disable-line sort-imports

import type { SubmitHandler } from 'react-hook-form';
import type { AddSystemApiRequest } from 'store'; // eslint-disable-line sort-imports

/** Shared license selector used by facility configuration. */
export const LicenseToggleGroup = (props: {
	defaultValue: string;
	onValueChange: (value: string) => void;
	disabled?: boolean;
	label: string;
	hideLabel: boolean;
	className?: string;
	options: {
		label: string;
		value: string;
		license_enum: number;
		active?: string;
	}[];
}) => {
	const { defaultValue, onValueChange, disabled, className, options } = props;
	const [toggleValue, setToggleValue] = useState(
		options.map((option) => ({
			...option,
			active: checkLicenseBits(defaultValue, option.license_enum) ? 'on' : 'off',
		}))
	);

	return (
		<RadixToggleGroup.Root
			className={classNames('flex h-8 font-primary text-i3', className)}
			type='single'
			disabled={disabled}
		>
			{toggleValue.map((option) => (
				<RadixToggleGroup.Item
					key={option.value}
					value={option.value}
					data-state={option.active}
					onClick={() => {
						setToggleValue((previousOptions) => {
							const nextOptions = previousOptions.map((currentOption) =>
								currentOption.value === option.value
									? { ...currentOption, active: currentOption.active === 'on' ? 'off' : 'on' }
									: currentOption
							);
							const combinedLicense = nextOptions
								.filter((currentOption) => currentOption.active === 'on')
								.map((currentOption) => parseInt(currentOption.value, 2))
								.reduce((accumulator, currentLicense) => accumulator | currentLicense, 0);

							onValueChange(intTo16BitBin(combinedLicense));
							return nextOptions;
						});
					}}
					className={classNames(
						'neighbor basis-full flex items-center justify-center border-y border-blue-500 bg-white relative min-w-[10em]',
						'first:border-l first:rounded-l-sm last:border-r last:rounded-r-sm',
						'focus:outline-none disabled:cursor-not-allowed disabled:opacity-30',
						'data-on:text-white data-on:bg-blue-500',
						'enabled:data-on:hover:bg-blue-700 enabled:data-on:hover:border-blue-700',
						'data-on:focus-visible:bg-blue-700 data-on:focus-visible:border-blue-700',
						'data-off:before:absolute data-off:before:left-0 data-off:before:top-0 data-off:before:w-px data-off:before:h-full data-off:before:bg-blue-100 disabled:data-off:before:bg-blue-400',
						'enabled:data-off:hover:bg-blue-100 data-off:focus-visible:bg-blue-100',
						'first:before:hidden neighbor-data-on:before:hidden'
					)}
				>
					{option.label}
				</RadixToggleGroup.Item>
			))}
		</RadixToggleGroup.Root>
	);
};

/** The two-step form used to provision a health system and its initial administrator. */
export const addSystemFormSchema = yup.object({
	systemName: yup
		.string()
		.trim()
		.matches(/^[a-zA-Z0-9 '-]+$/, 'Only letters, numbers, spaces, hyphens, and apostrophes are allowed')
		.required('A name is required to create a Health System')
		.test('valid', 'This field value is not valid.', requireValidFieldValue)
		.max(100, 'Health System name cannot be more than 100 characters.'),
	firstName: yup
		.string()
		.trim()
		.required('A first name is required')
		.max(100, 'First name cannot be more than 100 characters')
		.matches(inputFieldRegex, 'First Name is not valid.'),
	lastName: yup
		.string()
		.trim()
		.required('A last name is required')
		.max(100, 'Last name cannot be more than 100 characters')
		.matches(inputFieldRegex, 'Last Name is not valid.'),
	email: yup
		.string()
		.trim()
		.email('Please enter a valid email address')
		.required('A data handler email is required')
		.max(100, 'Email cannot be more than 100 characters'),
});

export type AddSystemFormInputs = yup.InferType<typeof addSystemFormSchema>;

export interface AddSystemDialogProps {
	children: React.ReactNode;
}

export function AddSystemDialog({ children }: AddSystemDialogProps) {
	const [step, setStep] = useState<1 | 2>(1);
	const [dialogOpen, setDialogOpen] = useState(false);
	const toast = useToast();
	const { getAlertResponse } = useAlert();
	const [createSystem, { isLoading, error }] = useAddSystemMutation();

	const {
		register,
		handleSubmit,
		reset,
		trigger,
		formState: { errors, isDirty },
	} = useForm<AddSystemFormInputs>({
		resolver: yupResolver(addSystemFormSchema),
		mode: 'onChange',
		defaultValues: {
			systemName: '',
			firstName: '',
			lastName: '',
			email: '',
		},
	});

	const resetDialog = () => {
		reset();
		setStep(1);
	};

	const onSubmit: SubmitHandler<AddSystemFormInputs> = async (formData) => {
		const requestBody: AddSystemApiRequest = {
			healthsystem_name: formData.systemName.trim(),
			data_handler: {
				first_name: formData.firstName.trim(),
				last_name: formData.lastName.trim(),
				email: formData.email.trim().toLowerCase(),
			},
		};

		const response = await createSystem(requestBody);

		handleApiResponse(response, {
			success: (payload) => {
				setDialogOpen(false);
				resetDialog();
				toast.createToast({
					title: `${payload.healthsystem_name} and its data handler were successfully created.`,
					variant: 'success',
				});
			},
			error: (payload) => {
				toast.createToast({ title: getQueryError(payload), variant: 'error' });
			},
		});
	};

	const continueToDataHandler = async () => {
		if (await trigger('systemName')) {
			setStep(2);
		}
	};

	const requestClose = async () => {
		if (!isDirty) {
			setDialogOpen(false);
			resetDialog();
			return;
		}

		const alertResponse = await getAlertResponse({
			description: "Closing the window will clear the information you've entered. Are you sure you'd like to continue?",
			responseOptions: [{ value: 'yes', label: 'Yes, close' }],
		});

		if (alertResponse === 'yes') {
			setDialogOpen(false);
			resetDialog();
		}
	};

	return (
		<Dialog
			actionButtons={
				step === 1
					? [{ children: 'Continue', onClick: continueToDataHandler }]
					: [
							{
								children: 'Create Health System',
								isWorking: isLoading,
								onClick: handleSubmit(onSubmit),
							},
							{
								children: 'Back',
								disabled: isLoading,
								onClick: () => setStep(1),
							},
					  ]
			}
			sizeX='sm'
			title='Add New Health System'
			subtitle={`Step ${step} of 2 — ${step === 1 ? 'Health System' : 'Data Handler'}`}
			trigger={children}
			open={dialogOpen}
			onOpenChange={(isOpen) => {
				if (isOpen) {
					setDialogOpen(true);
				} else {
					void requestClose();
				}
			}}
		>
			<form className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
				{step === 1 ? (
					<>
						<p className='text-p3 text-gray-500'>
							Enter the health system name exactly as it appears in the business agreement. This name will be shared with the
							client, so please ensure it is accurate.
						</p>
						<TextField
							label='Business Agreement Name'
							type='text'
							sizeY='sm'
							errorMessage={errors.systemName?.message}
							{...register('systemName')}
						/>
					</>
				) : (
					<>
						<p className='text-p3 text-gray-500'>
							This person will receive an invitation to access Merlin and upload data for the health system.
						</p>
						<div className='grid grid-cols-2 gap-3'>
							<TextField
								label='First Name'
								type='text'
								sizeY='sm'
								errorMessage={errors.firstName?.message}
								{...register('firstName')}
							/>
							<TextField
								label='Last Name'
								type='text'
								sizeY='sm'
								errorMessage={errors.lastName?.message}
								{...register('lastName')}
							/>
						</div>
						<TextField
							label='Data Handler Email'
							type='email'
							sizeY='sm'
							errorMessage={errors.email?.message}
							{...register('email')}
						/>
					</>
				)}
			</form>
			{error && <p className='mt-3 text-p2 text-red-400'>{getQueryError(error)}</p>}
		</Dialog>
	);
}

export default AddSystemDialog;
